import { Settings } from "luxon";
import { Fragment, useEffect, useState } from "react";
import { TimeoutWarningModal } from "react-common-ui-library";
import { useIdleTimer } from "react-idle-timer";
import { useDispatch, useSelector } from "react-redux";
import {
  LOGIN_ROUTE,
  PROMPT_BEFORE_IDLE,
  THROTTLE_TIME,
  TIMEOUT_VALUE,
  MODAL_TIMEOUT,
  PROVIDER_SITE_MANAGER,
} from "../Constants/CommonConstants.js";
import MyProfileWrapper from "./MyProfile/MyProfileWrapper.jsx";
import { manageAuthorizationSelector } from "../Selectors/AuthorizationSelector.js";
import { useNavigate } from "react-router-dom";
import {
  clearAuthorizationApiResponse,
  generateToken,
  performLogout,
  setIsEntitledStaff,
} from "../Features/AuthorizationSlice.js";
import { manageApiResponseSelector } from "../Selectors/ApiResponseSelector.js";
import {
  SLICE_AUTHORIZATION,
  THUNK_POST_REFRESH_JWT_TOKEN,
} from "../Constants/SliceAndThunkConstants.js";

/**
 * Timeout (in milliseconds) for idle detection
 */
const timeout = MODAL_TIMEOUT;

/**
 * Displays the timeout modal when user is idle for 13 minutes
 */
const promptBeforeIdle = PROMPT_BEFORE_IDLE;

/**
 * @description Contains app routes and timeout warning modal.
 * @returns {ReactNode} Render the MyProfileWrapper component and time warning modal functionality for user inactivity.
 */
function AuthorizationWrapper() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  /**
   * State for managing the visibility of the timeout modal
   */
  const [timeoutModalIsOpen, setTimeoutModalIsOpen] = useState(false);

  const { jwtToken, shiftedDateMillisFromEpoch, entitlementList } = useSelector(
    (state) => manageAuthorizationSelector(state)
  );

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_AUTHORIZATION,
      THUNK_POST_REFRESH_JWT_TOKEN
    )
  );

  const { isSuccess } = apiResponses;

  /**
   * @description Function to handle logout action.
   * @function setTimeoutModalIsOpen sets timeout modal to false on handle logout.
   * @event performLogout dispatch event to perform logout action.
   * @event navigate navigates to login route.
   */
  const handleLogout = () => {
    setTimeoutModalIsOpen(false);
    dispatch(performLogout());
    navigate(`/${LOGIN_ROUTE}`);
  };

  /**
   * @description Callback function to execute when user is idle for 15 minutes.
   * @function handleLogout execute handleLogout function when user is idle for 15 minutes.
   */
  const onIdle = () => {
    handleLogout();
  };

  /**
   * @description Callback function to execute when user clicks on continue.
   * @function setTimeoutModalIsOpen sets timeout modal to false when user is active.
   */
  const onActive = () => {
    setTimeoutModalIsOpen(false);
  };

  /**
   * @description Set the visibility of the timeout modal to true when user is idle for 13 minutes.
   * @function setTimeoutModalIsOpen sets timeout modal to true on when user is idle for 13 minutes.
   */
  const onPrompt = () => {
    setTimeoutModalIsOpen(true);
  };

  /**
   * @description IdleTimer hook that detects the user idle state.
   * @param onIdle Callback function to execute when user is idle.
   * @param onPrompt Set the visibility of the timeout modal to true when user is idle.
   * @param  onActive Callback function to execute when user clicks on continue.
   * @param  timeout Timeout (in milliseconds) for idle detection.
   * @param  promptBeforeIdle timer to display timeout modal when user is idle.
   * @param  throttle Throttles the idle time checking.
   * @returns {activate} Reset function that resets idle timer when the user clicks continue
   */
  const { activate } = useIdleTimer({
    onIdle,
    onPrompt,
    onActive,
    timeout,
    promptBeforeIdle,
    throttle: THROTTLE_TIME,
  });

  /**
   * @description Handler for continue button click in the timeout modal.
   * @event activate Resets the idle timer when the user clicks continue.
   */
  const continueButtonClickHandler = () => {
    activate();
  };

  /** use effect hook to refresh JWT token at 13 minutes interval */
  /**
   * @description use effect hook to refresh JWT token at 13 minutes interval.
   * @event generateToken dispatch event to refresh the jwt token for every 13 minutes.
   */
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(generateToken());
    }, TIMEOUT_VALUE);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    dispatch(generateToken());
  }, [dispatch]);

  useEffect(() => {
    if (shiftedDateMillisFromEpoch) {
      Settings.now = () => shiftedDateMillisFromEpoch;
    } else {
      Settings.now = () => Date.now();
    }
  }, [shiftedDateMillisFromEpoch]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(clearAuthorizationApiResponse(THUNK_POST_REFRESH_JWT_TOKEN));
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        setIsEntitledStaff(entitlementList?.includes(PROVIDER_SITE_MANAGER))
      );
    }
  }, [isSuccess, dispatch, entitlementList]);

  return (
    <Fragment>
      {jwtToken ? (
        <Fragment>
          <MyProfileWrapper />
          <TimeoutWarningModal
            open={timeoutModalIsOpen}
            continueButtonOnClick={continueButtonClickHandler}
            logoutButtonOnClick={handleLogout}
          />
        </Fragment>
      ) : null}
    </Fragment>
  );
}

export default AuthorizationWrapper;
